import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { getISOWeekKey, getWeekOffset } from "@/lib/streakUtils";

export function useStreak() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["user-streak", user?.id],
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("check_ins_visits")
        .select("checked_in_at")
        .eq("user_id", user!.id)
        .order("checked_in_at", { ascending: false });

      if (error) throw error;

      // Build set of week keys that have at least one check-in
      const weekSet = new Set<string>();
      for (const ci of data || []) {
        weekSet.add(getISOWeekKey(new Date(ci.checked_in_at)));
      }

      const now = new Date();
      const currentWeekKey = getISOWeekKey(now);
      const isActiveThisWeek = weekSet.has(currentWeekKey);

      // Count consecutive weeks walking backwards
      let streak = 0;
      let checkWeek = isActiveThisWeek ? currentWeekKey : getWeekOffset(currentWeekKey, -1);

      while (weekSet.has(checkWeek)) {
        streak++;
        checkWeek = getWeekOffset(checkWeek, -1);
      }

      // If not active this week, streak is from last week backwards
      // isBroken: last week had no check-in AND current week has none AND it's Monday
      const lastWeekKey = getWeekOffset(currentWeekKey, -1);
      const isMonday = now.getDay() === 1;
      const isBroken = !isActiveThisWeek && !weekSet.has(lastWeekKey) && isMonday;

      return { currentStreak: streak, isActiveThisWeek, isBroken, isLoading: false };
    },
  });
}

/**
 * Compute streak from check-ins for milestone checking (used after a check-in).
 * Returns the streak count including the current week.
 */
export async function computeStreakForUser(userId: string): Promise<number> {
  const { data } = await (supabase as any)
    .from("check_ins_visits")
    .select("checked_in_at")
    .eq("user_id", userId)
    .order("checked_in_at", { ascending: false });

  const weekSet = new Set<string>();
  for (const ci of data || []) {
    weekSet.add(getISOWeekKey(new Date(ci.checked_in_at)));
  }

  const currentWeekKey = getISOWeekKey(new Date());
  if (!weekSet.has(currentWeekKey)) return 0;

  let streak = 0;
  let checkWeek = currentWeekKey;
  while (weekSet.has(checkWeek)) {
    streak++;
    checkWeek = getWeekOffset(checkWeek, -1);
  }

  return streak;
}
